import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';

import {
  defer,
  from,
  iif,
  Observable, of,
  throwError,
  timer
} from 'rxjs';
import {
  catchError,
  first,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators';

import { AuthService } from '@auth0/auth0-angular';
import { Logger } from '@sondermind/datadog-logging';
import { ConfigurationService } from '@sondermind/configuration';
import { UrlTargetService } from '@sondermind/data-access/auth-utils';
import { Auth0TokenService } from '../services/auth0.service';
import { AuthTokenService } from '../services/auth_token.service';
import { UNAUTHORIZED_REDIRECT_ROUTE } from '../tokens/unauthorized-redirect-route.token';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authTokenService: AuthTokenService,
    private configurationService: ConfigurationService,
    private router: Router,
    private auth0Service: Auth0TokenService,
    @Optional()
    @Inject(UNAUTHORIZED_REDIRECT_ROUTE)
    private unauthorizedRedirectRoute: string | null,
    private auth0: AuthService,
    private urlTargetService: UrlTargetService,
    private logger: Logger
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.interceptCore(req, next, true);
  }

  private interceptCore(req: HttpRequest<any>, next: HttpHandler, retryOnFailure: boolean): Observable<HttpEvent<any>> {
    // bail if we're not requesting something against our api
    if (!this.urlTargetService.targetsSonderMindApis(req)) { return next.handle(req) }

    // bail if the request already has an authorization header
    // likely this is meant to skip token refreshes
    if (req.headers.has('Authorization') && !this.auth0Service.auth0Enabled) {
      return next.handle(req).pipe(
        catchError((e) => this.errorHandler(req, next, e, retryOnFailure))
      );
    }

    const origReq = req.clone();
    return this.auth0Service.auth0Enabled$.pipe(
      take(1),
      switchMap((enabled) => iif(
        () => enabled,
        defer(() => this.auth0Service.auth0TokenBehaviorSubject$.pipe(
          take(1),
          switchMap((token) => (token
            ? of(token)
            : from(this.auth0.getAccessTokenSilently({
              cacheMode: 'on',
              detailedResponse: true,
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configurationService.env.eeNamespace,
              },
            })).pipe(
              map((r) => r.access_token),
              tap((t) => this.auth0Service.auth0TokenBehaviorSubject$.next(t))
            ))
          )
        )),
        this.resolveOAuthToken()
      )),
      switchMap((token) => {
        const reqWithAuth = token
          ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
          : req;
        return next.handle(reqWithAuth);
      }),
      catchError((e) => this.errorHandler(origReq, next, e, retryOnFailure))
    );
  }
  private resolveAuth0Token$(): Observable<string> {
    return this.auth0Service.auth0Token$;
  }

  // if there's no provided token resolver, we do it naively with the oauth service
  private resolveOAuthToken(): Observable<string> {
    // if we have a valid token, use it
    if (this.authTokenService.isAuthenticated()) {
      return of(this.authTokenService.getAuthToken());
    }

    // otherwise do a silent refresh loop
    return from(this.authTokenService.silentRefresh()).pipe(
      map(() => this.authTokenService.getAuthToken())
    );
  }

  // reruns the given request after a delay if the failure was authentication related
  private errorHandler(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: any,
    retry: boolean
  ): Observable<HttpEvent<any>> {
    if (error.status === 401 || error.status === 403) {
      if (retry) {
        return timer(500).pipe(
          switchMap(() => this.interceptCore(req, next, false))
        );
      }

      if (this.auth0Service.auth0Enabled) {
        this.logger.info('Handling Auth0 authentication failure', { url: req.url });
        return this.auth0.isAuthenticated$.pipe(
          first(),
          switchMap((isAuthenticated) => {
            if (isAuthenticated) {
              this.logger.info('User is authenticated with Auth0, attempting to refresh token', { url: req.url });
              return this.auth0.getAccessTokenSilently({
                cacheMode: 'on',
                detailedResponse: true,
                authorizationParams: {
                  scope: 'offline_access',
                  eeNamespace: this.configurationService.env.eeNamespace
                }
              }).pipe(
                tap(() => this.logger.info('Successfully refreshed Auth0 token')),
                catchError((tokenError) => {
                  this.logger.error('Auth0 token refresh failed, logging out', { error: tokenError.message });
                  this.auth0Service.logout();
                  return throwError(() => new Error('Token refresh failed'));
                }),
                switchMap((tokenResponse) => {
                  this.auth0Service.auth0TokenBehaviorSubject$.next(tokenResponse.access_token);
                  this.logger.debug('Updated Auth0 token and retrying request', { url: req.url });

                  const newReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${tokenResponse.access_token}`
                    }
                  });

                  return next.handle(newReq);
                })
              );
            }
            return this.redirectAndLogout(req, error);
          })
        );
      }
      return this.redirectAndLogout(req, error);
    }

    return throwError(() => error);
  }

  private redirectAndLogout(req: HttpRequest<unknown>, error: unknown): Observable<null> {
    this.logger.debug('Logging out due to 401 or 403', { request: req, error });

    const redirectRoute = this.unauthorizedRedirectRoute
      || `${this.configurationService.env.authBase}/session/logout`;

    return of(this.router.navigateByUrl(redirectRoute)).pipe(
      tap(() => this.authTokenService.logout()),
      switchMap(() => of(null))
    );
  }
}
